
import React, { useState } from 'react';

interface CreateContextFormProps {
    onSubmit: (type: 'CONTENT' | 'PROCESS', options: string[], hours: number) => Promise<void>;
    onCancel: () => void;
}

export const CreateContextForm: React.FC<CreateContextFormProps> = ({
    onSubmit,
    onCancel
}) => {
    const [contextType, setContextType] = useState<'CONTENT' | 'PROCESS'>('CONTENT');
    const [options, setOptions] = useState<{ id: number; value: string }[]>([
        { id: 1, value: '' },
        { id: 2, value: '' }
    ]);
    const [expiresHours, setExpiresHours] = useState(24);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [nextId, setNextId] = useState(3); // Stable IDs to avoid index bugs

    // CRITICAL VALIDATION: Options must be unique (case-insensitive), trimmed, non-empty
    const validateOptions = (): { valid: boolean; cleanOptions: string[]; error?: string } => {
        const trimmed = options.map(o => o.value.trim()).filter(v => v.length > 0);

        // Check minimum
        if (trimmed.length < 2) {
            return { valid: false, cleanOptions: [], error: 'At least 2 options required' };
        }

        // Check maximum
        if (trimmed.length > 5) {
            return { valid: false, cleanOptions: [], error: 'Maximum 5 options allowed' };
        }

        // Check for duplicates (case-insensitive)
        const lowerCased = trimmed.map(o => o.toLowerCase());
        const uniqueSet = new Set(lowerCased);
        if (uniqueSet.size !== lowerCased.length) {
            return { valid: false, cleanOptions: [], error: 'Options must be unique (case-insensitive)' };
        }

        return { valid: true, cleanOptions: trimmed };
    };

    // Clamp expires hours to 1-168 range
    const clampedHours = Math.max(1, Math.min(168, expiresHours));

    const { valid: isValid, error: validationError } = validateOptions();

    const handleAddOption = () => {
        if (options.length < 5) {
            setOptions([...options, { id: nextId, value: '' }]);
            setNextId(nextId + 1);
        }
    };

    const handleRemoveOption = (id: number) => {
        if (options.length > 2) {
            setOptions(options.filter(o => o.id !== id));
        }
    };

    const handleOptionChange = (id: number, value: string) => {
        setOptions(options.map(o => o.id === id ? { ...o, value } : o));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validation = validateOptions();
        if (!validation.valid) {
            setError(validation.error || 'Invalid options');
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            await onSubmit(contextType, validation.cleanOptions, clampedHours);
            // Success - parent will handle UI update
        } catch (err: any) {
            // Propagate error detail from API (400/409/403)
            const detail = err.response?.data?.detail || err.message || 'Failed to create context';

            // Special handling for 409 (open context exists)
            if (err.response?.status === 409 || detail.toLowerCase().includes('open') || detail.toLowerCase().includes('exists')) {
                setError(`${detail}. Please close the existing context first.`);
            } else {
                setError(detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Context Type
                </label>

                <div className="space-y-2">
                    <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors">
                        <input
                            type="radio"
                            name="context-type"
                            value="CONTENT"
                            checked={contextType === 'CONTENT'}
                            onChange={(e) => setContextType(e.target.value as 'CONTENT')}
                            disabled={submitting}
                            className="mt-0.5 w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                            <div className="font-medium text-slate-900 dark:text-slate-100">CONTENT</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                Choose between alternative ideas/implementations
                            </div>
                        </div>
                    </label>

                    <label className="flex items-start gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors">
                        <input
                            type="radio"
                            name="context-type"
                            value="PROCESS"
                            checked={contextType === 'PROCESS'}
                            onChange={(e) => setContextType(e.target.value as 'PROCESS')}
                            disabled={submitting}
                            className="mt-0.5 w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                            <div className="font-medium text-slate-900 dark:text-slate-100">PROCESS</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                                Decide next action/workflow step
                            </div>
                        </div>
                    </label>
                </div>
            </div>

            <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Options (2-5 required, must be unique)
                </label>

                <div className="space-y-2">
                    {options.map((option, index) => (
                        <div key={option.id} className="flex items-center gap-2">
                            <input
                                type="text"
                                value={option.value}
                                onChange={(e) => handleOptionChange(option.id, e.target.value)}
                                disabled={submitting}
                                placeholder={`Option ${index + 1}`}
                                className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {options.length > 2 && (
                                <button
                                    type="button"
                                    onClick={() => handleRemoveOption(option.id)}
                                    disabled={submitting}
                                    className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                >
                                    Remove
                                </button>
                            )}
                        </div>
                    ))}
                </div>

                {options.length < 5 && (
                    <button
                        type="button"
                        onClick={handleAddOption}
                        disabled={submitting}
                        className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                    >
                        + Add Option
                    </button>
                )}

                {validationError && (
                    <div className="mt-2 text-xs text-red-600 dark:text-red-400">
                        {validationError}
                    </div>
                )}
            </div>

            <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">
                    Expires In (hours)
                </label>

                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        value={expiresHours}
                        onChange={(e) => setExpiresHours(parseInt(e.target.value) || 24)}
                        disabled={submitting}
                        min={1}
                        max={168}
                        className="w-24 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                        hours (1-168, default 24)
                    </span>
                </div>
            </div>

            {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
                    ❌ {error}
                </div>
            )}

            <div className="flex gap-2">
                <button
                    type="submit"
                    disabled={!isValid || submitting}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed transition-colors"
                >
                    {submitting ? 'Creating...' : 'Create Context'}
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={submitting}
                    className="px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </form>
    );
};
